import { useSelector, useDispatch } from 'react-redux';
import { PricingPlan, PricingPlanType } from '../types/pricingPlans';
import { useTranslation } from 'react-i18next';
import { updateSettings } from '../store/reducers/AccountSlice';
import SubscriptionsAPI from '../api/SubscriptionsAPI';
import { useEffect, useState } from 'react';
import { generatePricingPlan, getPlanConfig } from '../utils/pricingPlanUtils';

export const usePricingPlan = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector((state: any) => state.settings.value);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  
  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      if (!settings.isLoggedIn) return;
      
      setIsLoadingSubscription(true);
      try {
        const response = await SubscriptionsAPI.getCurrentSubscription();
        setCurrentSubscription(response.data);
        dispatch(updateSettings({ 
          pricingPlan: response.data.planId,
        }));
      } catch (error) {
        console.error('Failed to fetch current subscription:', error);
        setCurrentSubscription({ planId: 'free', status: 'active' });
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchCurrentSubscription();
  }, [settings.isLoggedIn, dispatch]);
  
  const getCurrentPlan = (): PricingPlan => {
    const user = settings;
    const currentPlanId = user.pricingPlan || 'free';
    const plan = generatePricingPlan(currentPlanId as PricingPlanType, true);
    
    return {
      ...plan,
      name: t(plan.name, plan.name),
      description: t(plan.description, plan.description),
      features: plan.features.map(featureKey => t(featureKey, featureKey)),
      featuresNotIncluded: plan.featuresNotIncluded.map(featureKey => t(featureKey, featureKey)),
      note: plan.note ? t(plan.note, plan.note) as string : undefined,
      price: plan.price === 'Free' ? 'Free' : t(plan.price, plan.price) as string
    };
  };

  const getAvailableUpgrades = (): PricingPlan[] => {
    const currentPlan = getCurrentPlan();
    if (!currentPlan.upgradeTo) return [];
    
    return currentPlan.upgradeTo.map(planId => {
      const plan = generatePricingPlan(planId, false);
      return {
        ...plan,
        name: t(plan.name, plan.name),
        description: t(plan.description, plan.description),
        features: plan.features.map(featureKey => t(featureKey, featureKey)),
        featuresNotIncluded: plan.featuresNotIncluded.map(featureKey => t(featureKey, featureKey)),
        note: plan.note ? t(plan.note, plan.note) as string : undefined,
        price: plan.price === 'Free' ? 'Free' : t(plan.price, plan.price) as string
      };
    });
  };

  const getPlanIcon = (planId: PricingPlanType) => {
    return getPlanConfig(planId).icon;
  };

  const getPlanColor = (planId: PricingPlanType) => {
    return getPlanConfig(planId).cssClasses;
  };

  const updatePlan = (newPlan: PricingPlanType) => {
    dispatch(updateSettings({ pricingPlan: newPlan }));
  };

  return {
    currentPlan: getCurrentPlan(),
    availableUpgrades: getAvailableUpgrades(),
    getPlanIcon,
    getPlanColor,
    updatePlan,
    isLoggedIn: settings.isLoggedIn,
    currentSubscription,
    isLoadingSubscription
  };
};
